import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import type { VariantType } from 'notistack';
import {
  Box, Card, CardHeader, CardContent, CardActions, Typography, IconButton, Chip, Tooltip,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Avatar, Divider, Grid, Badge, Button, TextField, InputAdornment
} from '@mui/material';
import {
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  LocationOn as LocationIcon, 
  CalendarToday as CalendarTodayIcon, 
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Hooks
import { useUsers } from '../../hooks/useUsers';
import { useRoles } from '../../hooks/useRoles';

// Types
import { User as BaseUser } from '../../types/user';
import UserForm from './UserForm';

// Extend the BaseUser type for our component needs
interface User extends Omit<BaseUser, 'name' | 'roleId' | 'firstName' | 'lastName' | 'email'> {
  isSystem?: boolean;
  name: string;
  role?: {
    id: string;
    name: string;
  };
  roleId: string;
  hospital?: {
    id: string;
    name: string;
  } | null;
  firstName: string;
  lastName: string;
  email: string;
}

// Main component
export const UsersList: React.FC = () => {
  // State
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(false);
  
  // Hooks
  const { enqueueSnackbar } = useSnackbar();
  const { users: usersData, deleteUser, fetchUsers, loading, error } = useUsers();
  const { roles } = useRoles();
  
  // Map users to include role information and format data
  const users = useMemo(() => {
    console.log('Raw usersData:', usersData);
    console.log('Available roles:', roles);
    
    return (usersData || []).map(user => {
      const name = user.name || [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || 'Unknown User';
      
      // Handle case where role is a string (SYSTEM_ADMIN, etc.)
      const roleString = typeof user.role === 'string' ? user.role : '';
      const roleId = user.roleId || (user.role && typeof user.role === 'object' ? user.role.id : roleString || '');
      
      // Initialize userRole based on the data we have
      let userRole = typeof user.role === 'string' 
        ? { id: user.role, name: user.role } 
        : user.role || (roleId ? { id: roleId, name: '' } : undefined);
      
      console.log(`Processing user ${user.id} (${name}):`, { 
        rawRole: user.role,
        roleId,
        userRole,
        rolesList: roles
      });
      
      // If we have a role ID but no name, try to find it in the roles list
      if (roleId && userRole) {
        const roleFromList = roles.find(r => r.id === roleId);
        console.log('Found role from list:', roleFromList);
        
        if (roleFromList) {
          userRole = {
            ...userRole,
            name: roleFromList.name || userRole.name || roleId
          };
        } else if (roleId && !userRole.name) {
          // If we couldn't find the role in the list but have a roleId, use that as the name
          userRole = {
            ...userRole,
            name: roleId.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')
          };
        }
        console.log('Updated userRole:', userRole);
      }
      
      // Determine if user is a system admin based on role name
      const isSystemAdmin = userRole?.name?.toUpperCase() === 'SYSTEM_ADMIN' || 
                          roleString?.toUpperCase() === 'SYSTEM_ADMIN';
      
      const processedUser = {
        ...user,
        name,
        roleId,
        role: userRole,
        isSystem: isSystemAdmin, // Set isSystem based on role
      } as User;
      
      console.log('Processed user:', { 
        id: processedUser.id,
        name: processedUser.name,
        role: userRole,
        isSystemAdmin,
        roleString
      });
      
      return processedUser;
    });
  }, [usersData, roles]);
  
  const showSnackbar = useCallback((message: string, variant: VariantType) => {
    enqueueSnackbar(message, { variant });
  }, [enqueueSnackbar]);
  
  // Fetch users on component mount
  useEffect(() => {
    console.log('Fetching users...');
    fetchUsers().then(() => {
      console.log('Users fetched successfully');
    }).catch(error => {
      console.error('Error fetching users:', error);
    });
  }, [fetchUsers]);

  const getRoleName = useCallback((user: User): string => {
    console.log('Getting role name for user:', { 
      userId: user.id, 
      userName: user.name,
      userRole: user.role,
      userRoleId: user.roleId,
      allRoles: roles
    });
    
    // First check if we have a role object with a name
    if (user.role?.name?.trim()) {
      console.log('Using role name from user.role:', user.role.name);
      return user.role.name;
    }
    
    // If not, try to find the role by ID in the roles list
    if (user.roleId) {
      console.log('Looking up role by ID:', user.roleId);
      const role = roles.find(r => r.id === user.roleId);
      console.log('Found role:', role);
      
      if (role?.name?.trim()) {
        console.log('Using role name from roles list:', role.name);
        return role.name;
      }
      
      // If we still don't have a name, return a formatted version of the role ID
      if (role) {
        const formattedId = role.id.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
        console.log('Formatted role ID:', formattedId);
        return formattedId;
      }
    }
    
    console.log('No role found, using fallback');
    return 'No role';
  }, [roles]);

  const filteredUsers = useMemo(() => {
    const searchLower = searchQuery.trim().toLowerCase();
    if (!searchLower) return users || [];
    
    return (users || []).filter(user => {
      const nameMatch = user.name?.toLowerCase().includes(searchLower) || false;
      const emailMatch = user.email?.toLowerCase().includes(searchLower) || false;
      const roleMatch = getRoleName(user).toLowerCase().includes(searchLower);
      
      return nameMatch || emailMatch || roleMatch;
    });
  }, [users, searchQuery, getRoleName]);

  const getRoleColor = (roleName: string): string => {
    if (!roleName) return 'default';
    
    const role = roleName.toLowerCase();
    switch (role) {
      case 'admin':
      case 'system_admin':
        return 'primary';
      case 'doctor':
        return 'secondary';
      case 'nurse':
        return 'success';
      case 'receptionist':
      case 'staff':
        return 'warning';
      case 'patient':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleAddNew = useCallback(() => {
    setUserFormOpen(true);
    setEditingUserId(null);
    setViewMode(false);
  }, []);

  const handleView = useCallback((user: User) => {
    setSelectedUser({
      ...user,
      role: user.role || (user.roleId ? { 
        id: user.roleId, 
        name: getRoleName(user) 
      } : undefined)
    });
    setViewMode(true);
  }, [getRoleName]);

  const handleEditUser = useCallback((user: User) => {
    setSelectedUser({
      ...user,
      role: user.role || (user.roleId ? { 
        id: user.roleId, 
        name: getRoleName(user) 
      } : undefined)
    });
    setUserFormOpen(true);
    setEditingUserId(user.id);
  }, [getRoleName]);

  const handleDeleteClick = useCallback((user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  }, []);

  const handleAssignLocation = useCallback((userId: string) => {
    // Implementation for assigning location
    enqueueSnackbar('Location assignment not implemented yet', { variant: 'info' });
  }, [enqueueSnackbar]);

  const handleConfirmDelete = useCallback(async () => {
    if (selectedUser?.id) {
      try {
        await deleteUser(selectedUser.id);
        showSnackbar('User deleted successfully', 'success');
        // Refresh users list
        fetchUsers();
      } catch (error) {
        showSnackbar('Failed to delete user', 'error');
      } finally {
        setDeleteDialogOpen(false);
        setSelectedUser(null);
      }
    }
  }, [selectedUser, deleteUser, fetchUsers, showSnackbar]);

  const viewLocation = useCallback((user: User) => {
    if (user.hospital?.name) {
      enqueueSnackbar(`Viewing location: ${user.hospital.name}`, { variant: 'info' });
    } else {
      enqueueSnackbar('No hospital assigned', { variant: 'info' });
    }
  }, [enqueueSnackbar]);

  const formatDate = useCallback((dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return 'Invalid date';
    }
  }, []);

  const getInitials = useCallback((name?: string): string => {
    if (!name?.trim()) return 'U';
    return name
      .split(' ')
      .filter(part => part.length > 0)
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  // Handle dialog close
  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  }, []);

  const handleCloseUserForm = useCallback(() => {
    setUserFormOpen(false);
    setViewMode(false);
    setEditingUserId(null);
    setSelectedUser(null);
  }, []);

  const handleUserFormSuccess = useCallback((userId: string) => {
    handleCloseUserForm();
    fetchUsers();
    showSnackbar(
      userId ? 'User updated successfully' : 'User created successfully', 
      'success'
    );
  }, [fetchUsers, showSnackbar, handleCloseUserForm]);

  // Handle password reset
  const handleResetPassword = useCallback(async (userId: string) => {
    try {
      // Implementation for password reset would go here
      // For now, we'll just show a success message
      showSnackbar('Password reset email has been sent', 'success');
    } catch (error) {
      console.error('Error resetting password:', error);
      showSnackbar('Failed to send password reset email', 'error');
    }
  }, [showSnackbar]);

  // Styles
  const styles = {
    card: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 1,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    },
    addCard: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 200,
      padding: 2,
      cursor: 'pointer',
      border: '2px dashed',
      borderColor: 'divider',
      borderRadius: 1,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        borderColor: 'primary.main',
        backgroundColor: 'action.hover',
        '& .MuiSvgIcon-root': {
          color: 'primary.main',
        },
      },
    },
    cardHeader: {
      p: 2,
      '& .MuiCardHeader-avatar': {
        mr: 1.5,
      },
      '& .MuiCardHeader-content': {
        overflow: 'hidden',
      },
      '& .MuiCardHeader-title': {
        fontWeight: 500,
        fontSize: '0.9375rem',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      '& .MuiCardHeader-subheader': {
        mt: 0.5,
        fontSize: '0.75rem',
        color: 'text.secondary',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    cardContent: {
      flexGrow: 1,
      p: 2,
      pt: 0,
    },
    cardFooter: {
      p: 1.5,
      pt: 1,
      borderTop: '1px solid',
      borderColor: 'divider',
      '& .MuiButton-root': {
        minWidth: 'auto',
        p: '4px 8px',
        borderRadius: 1,
        '& + .MuiButton-root': {
          ml: 1,
        },
      },
    },
    roleChip: {
      height: 22,
      borderRadius: 1,
      fontSize: '0.6875rem',
      '& .MuiChip-label': {
        px: 1.25,
      },
      '&.MuiChip-outlined': {
        borderWidth: 1,
      },
    },
    actionButton: {
      width: 30,
      height: 30,
      p: 0.5,
      '&:hover': {
        backgroundColor: 'action.hover',
      },
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
        <CircularProgress size={40} thickness={4} color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1800, mx: 'auto' }}>
      {/* Header and Search */}
      <Box 
        sx={{ 
          mb: 3, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          User Management
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search users..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: { xs: '100%', sm: 280 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              backgroundColor: 'background.paper',
              '& fieldset': {
                borderColor: 'divider',
              },
              '&:hover fieldset': {
                borderColor: 'primary.light',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiInputBase-input': {
              py: 1,
              fontSize: '0.875rem',
            },
          }}
        />
      </Box>

      {error ? (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': { 
              width: '100%',
              py: 1,
            },
            '& .MuiAlert-action': { 
              alignItems: 'center',
              pl: 0,
            },
          }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={fetchUsers}
              startIcon={<RefreshIcon fontSize="small" />}
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                textTransform: 'none',
              }}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {/* Add User Card */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Card 
              onClick={handleAddNew}
              elevation={0}
              sx={styles.addCard}
            >
              <AddIcon 
                sx={{ 
                  fontSize: 40, 
                  mb: 1.5, 
                  color: 'text.secondary',
                  transition: 'all 0.2s ease-in-out',
                }} 
              />
              <Typography 
                variant="subtitle1" 
                color="text.secondary"
                sx={{ 
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                Add New User
              </Typography>
            </Card>
          </Grid>

          {/* User Cards */}
          {filteredUsers.map((user) => (
            <Grid item key={user.id} xs={12} sm={6} md={4} lg={3}>
              <Card sx={styles.card}>
                <CardHeader
                  avatar={
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      color={user.isActive ? 'success' : 'error'}
                      sx={{
                        '& .MuiBadge-badge': {
                          width: 10,
                          height: 10,
                          minWidth: 10,
                          borderRadius: '50%',
                          border: '2px solid white',
                        },
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 44,
                          height: 44,
                          fontSize: '0.9375rem',
                          fontWeight: 500,
                        }}
                      >
                        {getInitials(user.name)}
                      </Avatar>
                    </Badge>
                  }
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" noWrap sx={{ fontSize: '0.9375rem', fontWeight: 500 }}>
                        {user.name}
                      </Typography>
                      {user.isSystem && (
                        <Chip 
                          label="System" 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                          sx={{ 
                            ml: 1,
                            height: 18,
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            '& .MuiChip-label': {
                              px: 0.75,
                              py: 0.25,
                            },
                          }} 
                        />
                      )}
                    </Box>
                  }
                  subheader={
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8125rem' }}>
                      {user.email}
                    </Typography>
                  }
                  sx={styles.cardHeader}
                />
                
                <CardContent sx={styles.cardContent}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    mb: 1.5,
                    flexWrap: 'wrap',
                  }}>
                    <Chip 
                      label={getRoleName(user) || 'No role'}
                      size="small"
                      color={getRoleColor(getRoleName(user)) as any}
                      variant="outlined"
                      sx={{
                        ...styles.roleChip,
                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.08)' 
                          : 'rgba(0, 0, 0, 0.04)',
                      }}
                    />
                    <Chip 
                      label={user.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={user.isActive ? 'success' : 'error'}
                      variant="outlined"
                      sx={{
                        ...styles.roleChip,
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon 
                        fontSize="small" 
                        color="action" 
                        sx={{ opacity: 0.7, width: 16, height: 16 }} 
                      />
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8125rem' }}>
                        {user.lastLogin ? formatDate(user.lastLogin) : 'Never logged in'}
                      </Typography>
                    </Box>
                    
                    {user.hospital && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon 
                          fontSize="small" 
                          color="action" 
                          sx={{ opacity: 0.7, width: 16, height: 16 }} 
                        />
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8125rem' }}>
                          {user.hospital.name}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
                
                <Divider sx={{ my: 0 }} />
                
                <CardActions sx={styles.cardFooter}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="View">
                      <IconButton 
                        size="small" 
                        onClick={() => handleView(user)}
                        sx={styles.actionButton}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Assign Location">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleAssignLocation(user.id)}
                        sx={styles.actionButton}
                      >
                        <LocationIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {user.isSystem && (
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small"
                          onClick={() => handleEditUser(user)}
                          sx={styles.actionButton}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* User Form Dialog */}
      <UserForm
        open={userFormOpen || viewMode}
        onClose={handleCloseUserForm}
        userId={selectedUser?.id}
        mode={viewMode ? 'view' : (editingUserId ? 'edit' : 'create')}
        onSuccess={handleUserFormSuccess}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1.5,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.125rem',
          fontWeight: 600,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          Delete User
        </DialogTitle>
        <DialogContent sx={{ py: 2.5 }}>
          <DialogContentText sx={{ fontSize: '0.9375rem', color: 'text.primary' }}>
            Are you sure you want to delete {selectedUser?.name || 'this user'}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            size="small"
            sx={{
              px: 2,
              py: 0.75,
              fontSize: '0.8125rem',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: 1,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error"
            variant="contained"
            disabled={!selectedUser}
            size="small"
            sx={{
              px: 2,
              py: 0.75,
              fontSize: '0.8125rem',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: 1,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: 'error.dark',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Default export
export default UsersList;
